/**
 * Non-planner guided tax filings under SFrame207 — rolls up checklist frames into debts-taxes
 * step dt-4 (Identify Tax Filings).
 */

export const TAX_GUIDED_CHILD_SPECS = [
  { frameId: 'sframe-208', checkIds: ['i8-1', 'i8-2', 'i8-3'] as const },
  { frameId: 'sframe-209', checkIds: ['i9-1', 'i9-2', 'i9-3'] as const },
  { frameId: 'sframe-210', checkIds: ['i10-1', 'i10-2', 'i10-3'] as const },
  { frameId: 'sframe-212', checkIds: ['i11-1', 'i11-2', 'i11-3'] as const },
  { frameId: 'sframe-214', checkIds: ['i12-1', 'i12-2', 'i12-3'] as const },
] as const;

export const TAX_GUIDED_TOTAL = TAX_GUIDED_CHILD_SPECS.length;

const PLANNER_IDENTIFY_FILING_CARD_IDS = ['pf-1040', 'pf-1041', 'pf-706'] as const;

function rowDone(checks: Record<string, boolean> | undefined, ids: readonly string[]): boolean {
  return ids.length > 0 && ids.every((id) => !!checks?.[id]);
}

function rowStarted(checks: Record<string, boolean> | undefined, ids: readonly string[]): boolean {
  return ids.some((id) => !!checks?.[id]);
}

/** Per-guided-row completion (whole checklist done for each sub-frame) */
export async function getGuidedTaxRowCompletion(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>
): Promise<Record<string, boolean>> {
  const out: Record<string, boolean> = {};
  for (const spec of TAX_GUIDED_CHILD_SPECS) {
    const fd = await loadFrameData(spec.frameId);
    const checks = fd?.data?.checks as Record<string, boolean> | undefined;
    out[spec.frameId] = rowDone(checks, spec.checkIds);
  }
  return out;
}

/** Count of guided rows with all checklist items checked */
export async function countTaxGuidedSubstepsDone(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>
): Promise<number> {
  let complete = 0;
  for (const spec of TAX_GUIDED_CHILD_SPECS) {
    const fd = await loadFrameData(spec.frameId);
    const checks = fd?.data?.checks as Record<string, boolean> | undefined;
    if (rowDone(checks, spec.checkIds)) complete += 1;
  }
  return complete;
}

/**
 * Reconcile Identify Tax Filings (dt-4) from all guided checklist frames.
 * Keeps backwards-compatible export name `syncDt4GuidedFromChildren`.
 */
export async function syncDt4GuidedFromChildren(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>
): Promise<number> {
  let fullyDoneRows = 0;
  let partialOrAnyIncompleteWork = false;
  for (const spec of TAX_GUIDED_CHILD_SPECS) {
    const fd = await loadFrameData(spec.frameId);
    const checks = fd?.data?.checks as Record<string, boolean> | undefined;
    const doneAll = rowDone(checks, spec.checkIds);
    const started = rowStarted(checks, spec.checkIds);
    if (doneAll) fullyDoneRows += 1;
    else if (started) partialOrAnyIncompleteWork = true;
  }
  const total = TAX_GUIDED_CHILD_SPECS.length;
  const phase = 'debts-taxes';
  let status: 'not-started' | 'in-progress' | 'complete';
  if (total > 0 && fullyDoneRows >= total) status = 'complete';
  else if (fullyDoneRows > 0 || partialOrAnyIncompleteWork) status = 'in-progress';
  else status = 'not-started';
  await updateStepProgress('dt-4', phase, { status });
  return fullyDoneRows;
}

export type PlannerTaxCardForSync = {
  id: string;
  cardComplete: boolean;
  filingStatus: string;
  summary: string;
  uploads: readonly { name?: string }[];
};

export function plannerCardToStepStatus(card: PlannerTaxCardForSync): 'not-started' | 'in-progress' | 'complete' {
  if (card.cardComplete) return 'complete';
  const hasWork =
    !!card.summary?.trim() ||
    card.uploads.length > 0 ||
    !!(card.filingStatus && card.filingStatus.trim() !== '' && card.filingStatus.trim() !== 'Not started');
  return hasWork ? 'in-progress' : 'not-started';
}

function rollupPlannerCardsIdentifyTaxFilingsFromCards(cards: PlannerTaxCardForSync[]): 'not-started' | 'in-progress' | 'complete' {
  const relevant = PLANNER_IDENTIFY_FILING_CARD_IDS.map((id) => cards.find((c) => c.id === id)).filter(
    Boolean
  ) as PlannerTaxCardForSync[];
  if (relevant.length === 0) return 'not-started';
  const statuses = relevant.map(plannerCardToStepStatus);
  if (statuses.every((s) => s === 'complete')) return 'complete';
  if (statuses.some((s) => s === 'in-progress' || s === 'complete')) return 'in-progress';
  return 'not-started';
}

/** Sync planner filing-card row state onto dt-4 (Identify Tax Filings — single rollup step). */
export async function syncPlannerFilingCardsToTaxSteps(
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>,
  cards: PlannerTaxCardForSync[]
): Promise<void> {
  const phase = 'debts-taxes';
  await updateStepProgress('dt-4', phase, {
    status: rollupPlannerCardsIdentifyTaxFilingsFromCards(cards),
  });
}
